/**
 * SIP Tab Lock — "Last Tab Wins" coordination.
 *
 * Ensures only one browser tab holds an active SIP registration at a time.
 * When a new tab wants to connect, it requests a takeover:
 *   - If the holding tab has NO active call → it disconnects, releases the lock,
 *     and the new tab acquires it seamlessly.
 *   - If the holding tab HAS an active call → takeover is denied and the
 *     requesting tab receives a clear SIP_ACTIVE_CALL_OTHER_TAB error.
 *     The holding tab KEEPS the lock (no state corruption).
 *
 * Communication: BroadcastChannel (primary) with localStorage fallback.
 * Lock primitive: Web Locks API (primary) with localStorage heartbeat fallback.
 */

const LOCK_NAME = 'omniplay-sip-registration';
const CHANNEL_NAME = 'omniplay-sip-coordination';
const LS_KEY = 'omniplay-sip-lock';
const LS_TAKEOVER_KEY = 'omniplay-sip-takeover';
const LS_HEARTBEAT_MS = 5000;
const LS_STALE_MS = 12000;
const TAKEOVER_TIMEOUT_MS = 2000;
const TAKEOVER_SETTLE_MS = 300;
const RETRY_DELAY_MS = 1500;

let lockHeld = false;
let heartbeatTimer = null;
let webLockResolver = null;
let takeoverHandler = null;
let channel = null;
let listenersInstalled = false;
const tabId = `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

// ── Lifecycle ──────────────────────────────────────────────────────

function ensureListeners() {
  if (listenersInstalled) return;
  listenersInstalled = true;

  window.addEventListener('beforeunload', () => releaseSipLock());

  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = handleChannelMessage;
  } catch {
    window.addEventListener('storage', handleStorageTakeover);
  }
}

// ── BroadcastChannel handler ───────────────────────────────────────

function handleChannelMessage(event) {
  const { type, fromTabId } = event.data || {};
  if (!type || !fromTabId || fromTabId === tabId) return;

  if (type === 'takeover' && lockHeld) {
    const allowed = takeoverHandler ? takeoverHandler() : true;
    if (allowed) {
      releaseSipLock();
    }
    channel.postMessage({
      type: allowed ? 'takeover-ack' : 'takeover-denied',
      fromTabId: tabId,
      toTabId: fromTabId,
    });
  }
}

// ── localStorage fallback handler ──────────────────────────────────

function handleStorageTakeover(e) {
  if (e.key !== LS_TAKEOVER_KEY || !lockHeld) return;
  try {
    const data = JSON.parse(e.newValue);
    if (!data?.tabId || data.tabId === tabId) return;
    const allowed = takeoverHandler ? takeoverHandler() : true;
    if (allowed) {
      releaseSipLock();
    }
    localStorage.setItem(
      `${LS_TAKEOVER_KEY}-ack`,
      JSON.stringify({ tabId, allowed, toTabId: data.tabId, timestamp: Date.now() })
    );
  } catch { /* ignore parse errors */ }
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Register a callback invoked when another tab requests takeover.
 * Return `true` to allow (disconnect gracefully) or `false` to deny
 * (e.g. active call). When denied, the lock is KEPT by this tab.
 */
export function setTakeoverHandler(handler) {
  takeoverHandler = handler;
  ensureListeners();
}

/**
 * Acquire the SIP registration lock.
 *
 * @param {Object} opts
 * @param {boolean} opts.steal  If true, request takeover from the tab
 *   that currently holds the lock. User-initiated connects should pass
 *   true; automatic reconnects should pass false.
 * @returns {Promise<boolean>} true if acquired
 * @throws {Error} SIP_ACTIVE_CALL_OTHER_TAB if steal was requested but
 *   the holding tab had an active call.
 */
export async function acquireSipLock({ steal = false } = {}) {
  if (lockHeld) return true;
  ensureListeners();

  if (navigator.locks) {
    try {
      const first = await tryAcquireWebLock();
      if (first) return true;

      if (steal) return await requestTakeover();

      await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
      return await tryAcquireWebLock();
    } catch (e) {
      if (e.message === 'SIP_ACTIVE_CALL_OTHER_TAB') throw e;
      console.warn('[SIP Lock] Web Locks API failed, falling back:', e);
      return acquireFallbackLock();
    }
  }

  const first = acquireFallbackLock();
  if (first) return true;
  if (steal) return await requestTakeover();
  return false;
}

export function releaseSipLock() {
  lockHeld = false;
  if (webLockResolver) {
    webLockResolver();
    webLockResolver = null;
  }
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  try {
    const current = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (current?.tabId === tabId) localStorage.removeItem(LS_KEY);
  } catch { /* ignore */ }
}

export function hasSipLock() {
  return lockHeld;
}

// ── Web Lock helpers ───────────────────────────────────────────────

async function tryAcquireWebLock() {
  return new Promise((resolve, reject) => {
    navigator.locks
      .request(LOCK_NAME, { ifAvailable: true }, (lock) => {
        if (lock) {
          lockHeld = true;
          resolve(true);
          return new Promise((release) => {
            webLockResolver = release;
          });
        }
        resolve(false);
      })
      .catch(reject);
  });
}

// ── localStorage lock fallback ─────────────────────────────────────

function acquireFallbackLock() {
  try {
    const existing = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (
      existing &&
      existing.tabId !== tabId &&
      Date.now() - existing.timestamp < LS_STALE_MS
    ) {
      return false;
    }
    localStorage.setItem(LS_KEY, JSON.stringify({ tabId, timestamp: Date.now() }));
    lockHeld = true;
    startFallbackHeartbeat();
    return true;
  } catch {
    return false;
  }
}

function forceAcquireFallbackLock() {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ tabId, timestamp: Date.now() }));
    lockHeld = true;
    startFallbackHeartbeat();
    return true;
  } catch {
    return false;
  }
}

function startFallbackHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  heartbeatTimer = setInterval(() => {
    try {
      const cur = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (cur?.tabId === tabId) {
        localStorage.setItem(LS_KEY, JSON.stringify({ tabId, timestamp: Date.now() }));
      } else {
        lockHeld = false;
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    } catch {
      lockHeld = false;
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }, LS_HEARTBEAT_MS);
}

// ── Takeover negotiation ───────────────────────────────────────────

async function requestTakeover() {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;

    const settle = async (denied, timedOut = false) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);

      if (denied) {
        reject(new Error('SIP_ACTIVE_CALL_OTHER_TAB'));
        return;
      }

      await new Promise(r => setTimeout(r, TAKEOVER_SETTLE_MS));

      try {
        let acquired;
        if (navigator.locks) {
          acquired = await tryAcquireWebLock();
        } else if (timedOut) {
          acquired = forceAcquireFallbackLock();
        } else {
          acquired = acquireFallbackLock();
        }
        resolve(acquired);
      } catch {
        resolve(false);
      }
    };

    if (channel) {
      const handler = (event) => {
        const { type, toTabId } = event.data || {};
        if (toTabId !== tabId) return;
        if (type === 'takeover-ack') {
          channel.removeEventListener('message', handler);
          settle(false);
        } else if (type === 'takeover-denied') {
          channel.removeEventListener('message', handler);
          settle(true);
        }
      };
      channel.addEventListener('message', handler);
      channel.postMessage({ type: 'takeover', fromTabId: tabId });

      timeoutId = setTimeout(() => {
        channel.removeEventListener('message', handler);
        settle(false, true);
      }, TAKEOVER_TIMEOUT_MS);
    } else {
      try {
        localStorage.setItem(
          LS_TAKEOVER_KEY,
          JSON.stringify({ tabId, timestamp: Date.now() })
        );
      } catch { /* ignore */ }

      const ackHandler = (e) => {
        if (e.key !== `${LS_TAKEOVER_KEY}-ack`) return;
        try {
          const data = JSON.parse(e.newValue);
          if (data?.toTabId !== tabId) return;
          window.removeEventListener('storage', ackHandler);
          settle(!data.allowed);
        } catch { /* ignore */ }
      };
      window.addEventListener('storage', ackHandler);

      timeoutId = setTimeout(() => {
        window.removeEventListener('storage', ackHandler);
        settle(false, true);
      }, TAKEOVER_TIMEOUT_MS);
    }
  });
}
